import Api from '@sendsprint/api-types';
import { DURATION_1_MIN, DURATION_4_MIN, DURATION_5_MIN } from '@src/constants';
import { useAppLocation } from '@src/hooks/utils/useAppLocation';
import {
  useLogout,
  useRefreshToken,
  useSetAddress,
  useVeriffSessions,
  useVeriffSessionsTest
} from '@src/hooks/queries/auth';
import { Path } from '@src/navigations/routes';
import { getLoggedInUserInfo, persistLoggedInState } from '@src/services/auth-service';
import ClientApi from '@src/types/client';
import { clearState, getState } from '@src/utils/stateStorage';
import { isUndefined } from '@src/utils/type';
import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { getFullName } from '@src/utils/user';
import { useEnv } from './env-context';
import { useGetProfileDetails } from '@src/hooks/queries/user';

interface AuthProviderProps {
  children?: ReactNode;
}

export type BusinessVariant = 'receiver' | 'sender' | 'remittance' | null;

export type AuthContextI = {
  userAddress: ClientApi.General.UserAddress | null;
  setAddress: (
    form: ClientApi.Account.SetAddress.Request
  ) => Promise<ClientApi.General.UserAddress>;
  user: ClientApi.General.UserProfile | null;
  business: ClientApi.General.BusinessProfile | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logout(saveLocationState?: boolean): Promise<any>;
  handleLoginSuccess: (loggedInData: ClientApi.General.LoggedInUser) => void;
  isLoggedIn: boolean;
  businessVariant: BusinessVariant;
  handleLogoutSuccess: (state?: ClientApi.General.NotLoggedInLocationState) => void;
  logoutLoading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  veriffSessions: (data: any) => any;
  handleProfileUpdate: (data: ClientApi.User.GetProfileDetails.Response['data']) => void;
  handleBusinessUpdate: (data: ClientApi.User.GetBusinessDetails.Response['data']) => void;
  tokens: ClientApi.General.TokenDetails | null;
  isRemittanceBusiness: boolean;
  setAddressLoading: boolean;
  isWhiteLabelBusiness: boolean;
};

const resolveBusinessVariant = (
  userAddress: ClientApi.General.UserAddress | null
): BusinessVariant => {
  if (!userAddress) return null;

  switch (userAddress.country) {
    case Api.Model.CountryInitials.Canada:
    case Api.Model.CountryInitials.UnitedKingdom:
    case Api.Model.CountryInitials.UnitedStates:
      return 'sender';

    case Api.Model.CountryInitials.Nigeria:
    case Api.Model.CountryInitials.NigeriaNig:
    case Api.Model.CountryInitials.Kenya:
    case Api.Model.CountryInitials.Ghana:
    case Api.Model.CountryInitials.SouthAfrica:
      return 'receiver';

    default:
      return null;
  }
};

const AuthContext = createContext<AuthContextI | undefined>(undefined);

const AuthProvider = ({ children }: AuthProviderProps) => {
  const userInfo = getLoggedInUserInfo();
  const [tokens, setTokens] = useState(userInfo.tokens);
  const [user, setUser] = useState(userInfo.user);
  const [business, setBusiness] = useState(userInfo.business);
  const [userAddress, setUserAddress] = useState<ClientApi.General.UserAddress | null>(
    userInfo.address
  );
  const [refreshTokenTime, setRefreshTokenTime] = useState<number>(DURATION_5_MIN);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useAppLocation();
  const { IS_PROD } = useEnv();
  const isRemittanceBusiness = user?.userCategoryType === 3;
  const isWhiteLabelBusiness = user?.userCategoryType === 4;

  const { mutateAsync: setAddressMutate, isLoading: setAddressLoading } = useSetAddress();
  const { mutateAsync: logoutMutate, isLoading: logoutLoading } = useLogout();
  const { mutateAsync: veriffSessionsMutate } = useVeriffSessions();
  const { mutateAsync: veriffSessionsTestMutate } = useVeriffSessionsTest();

  const memoizedRefreshTime = useMemo(() => {
    return refreshTokenTime;
  }, [refreshTokenTime]);

  useRefreshToken(
    { refreshToken: tokens?.refreshToken || '' },
    {
      enabled: !!user && !!tokens?.refreshToken,
      refetchIntervalInBackground: true,
      refetchInterval: memoizedRefreshTime,
      staleTime: DURATION_4_MIN,
      retry: false,
      onSuccess: (data) => {
        handleUpdateToken(data);
      }
    }
  );

  useGetProfileDetails({
    enabled: !!user && user.userOnboardingStatus === 2,
    onSuccess: (data) => {
      handleProfileUpdate(data);
    }
  });

  const handleGetExpiryTime = (time: string) => {
    const currentMillisconds = new Date().getTime();
    const expiryTimeMilliseconds = new Date(time).getTime();

    const millisecondsDifference = expiryTimeMilliseconds - currentMillisconds;
    const deductedTime = millisecondsDifference - DURATION_1_MIN;

    return deductedTime;
  };

  const handleUpdateToken = (data: ClientApi.General.TokenDetails) => {
    const state = getState();
    const expiryTime = handleGetExpiryTime(data?.token?.expiryTime || '');

    if (state) {
      const hash = state?.tokens?.hash || '';
      const updatedTokens = { ...data, hash };
      setTokens(updatedTokens);
      setRefreshTokenTime(expiryTime);
      persistLoggedInState({
        user: state.user,
        address: state.address,
        tokens: updatedTokens,
        business: state.business
      } as ClientApi.General.LoggedInUser);
    }
  };

  const handleProfileUpdate = (data: ClientApi.User.GetProfileDetails.Response['data']) => {
    const state = getState();
    if (state) {
      const user = state.user as ClientApi.General.UserProfile;
      const userAddress = state.address as ClientApi.General.UserAddress;
      const updatedUser: ClientApi.General.UserProfile = {
        ...user,
        email: data.emailAddress,
        firstName: data.firstName,
        lastName: data.lastName,
        fullName: getFullName({ firstName: data.firstName, lastName: data.lastName }),
        profilePixUrl: data.profilePixUrl
      };
      const updatedUserAddress: ClientApi.General.UserAddress = {
        ...userAddress,
        phone: data.phoneNumber || ''
      };
      setUser(updatedUser);
      setUserAddress(updatedUserAddress);
      persistLoggedInState({
        user: updatedUser,
        address: updatedUserAddress,
        tokens: state.tokens,
        business: state.business
      } as ClientApi.General.LoggedInUser);
    }
  };

  const handleBusinessUpdate = (data: ClientApi.User.GetBusinessDetails.Response['data']) => {
    const state = getState();
    if (state) {
      const business = state.business as ClientApi.General.BusinessProfile;
      const updatedBusiness: ClientApi.General.BusinessProfile = {
        ...business,
        businessLogoUrl: data.businessLogoUrl,
        businessName: data.businessName
      };
      setBusiness(updatedBusiness);
      persistLoggedInState({
        user: state.user,
        address: state.address,
        tokens: state.tokens,
        business: updatedBusiness
      } as ClientApi.General.LoggedInUser);
    }
  };

  const handleLoginSuccess = useCallback((loggedInData: ClientApi.General.LoggedInUser) => {
    const { address, user, tokens, business } = loggedInData;

    setUser(user);
    setUserAddress(address);
    setTokens(tokens);
    setBusiness(business);
    persistLoggedInState(loggedInData);
    // const businessVariant = resolveBusinessVariant(address);
    // const isRemittance = user.userCategoryType === 3;
    // const { data } = await refetch();

    // if (
    //   data &&
    //   data.isBusinessInformationCompleted &&
    //   data.isBusinessAddressCompleted &&
    //   data.isBusinessDocumentCompleted &&
    //   data.isControlPersonCompleted
    // ) {
    //   if (businessVariant === 'receiver') {
    //     navigate(Path.ReceiverDashboard);
    //   } else if (isRemittance) {
    //     navigate(Path.SenderSetupGuide);
    //   } else if (businessVariant === 'sender') {
    //     navigate(Path.SenderDashboard);
    //   }
    // } else {
    //   navigate(Path.Onboarding);
    // }
  }, []);

  const setAddress = useCallback((form: ClientApi.Account.SetAddress.Request) => {
    return setAddressMutate(form).then((address) => {
      setUserAddress(address);

      return address;
    });
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const logout = async (saveLocationState: boolean) => {
    let state: ClientApi.General.NotLoggedInLocationState | undefined;
    if (saveLocationState) {
      state = { from: location };
    }
    queryClient.invalidateQueries();

    return logoutMutate().then(() => {
      handleLogoutSuccess(state);
    });
  };

  const handleLogoutSuccess = (state?: ClientApi.General.NotLoggedInLocationState) => {
    setUser(null);
    setTokens(null);
    setUserAddress(null);
    clearState();
    queryClient.clear();

    navigate(Path.Login, { state });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const veriffSessions = useCallback((payload: any) => {
    return veriffSessionsMutate({ data: payload })
      .then((data) => {
        return data.data;
      })
      .catch((e) => {
        return Promise.reject(e);
      });
  }, []);

  const value: AuthContextI = {
    isLoggedIn: !!user,
    businessVariant: resolveBusinessVariant(userAddress),
    userAddress,
    setAddress,
    logout,
    user,
    business,
    handleLogoutSuccess,
    handleLoginSuccess,
    logoutLoading,
    veriffSessions,
    handleProfileUpdate,
    handleBusinessUpdate,
    tokens,
    isRemittanceBusiness,
    setAddressLoading,
    isWhiteLabelBusiness
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAccount = () => {
  const context = useContext(AuthContext);
  if (isUndefined(context)) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }
  return context;
};

export default AuthProvider;
